import { useCallback, useEffect, useMemo, useState } from "react";
import API from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Lightning, MagnifyingGlass, CaretLeft, CaretRight, Copy, LinkSimple, DownloadSimple, CheckCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const CURRENCY_CHIP = "EUR";
// Full ISO-3166-1 alpha-2 list so the publisher catalog filter can hit
// any country the Yieldkit catalog covers. Ordered with the top 20
// markets first (matches admin defaults), then alphabetical for the
// long tail. "ALL" sentinel maps to no country filter on the API.
const COUNTRIES = [
  "ALL",
  // Top markets
  "US","GB","DE","FR","IT","ES","NL","BE","AT","CH","PL","SE","NO","DK","FI","IE","PT","AU","CA","JP",
  // Long tail (alphabetical)
  "AE","AR","BG","BR","CL","CN","CO","CZ","EE","GR","HK","HR","HU","ID","IL","IN","IS","KR","LT","LU",
  "LV","MA","MX","MY","NZ","PE","PH","RO","RS","RU","SA","SG","SI","SK","TH","TR","TW","UA","VN","ZA",
];

const ALL = "__ALL__";

// Publisher CPC page — Yieldkit brand catalog + clicks + commissions.
// Only reachable when admin has bound a YK account to this publisher
// (products_enabled includes "cpc"). PubLayout gates the nav entry; this
// page also self-gates with the server's 403 so direct URL access is safe.
export default function PubCPC() {
  const [tab, setTab] = useState("brands");
  const [accessDenied, setAccessDenied] = useState(false);
  const [filterOpts, setFilterOpts] = useState({ advertisers: [], states: [], account_label: "" });
  const [exportingClicks, setExportingClicks] = useState(false);
  const [exportingComms, setExportingComms] = useState(false);

  // Brands state
  const [brands, setBrands] = useState([]);
  const [brandsTotal, setBrandsTotal] = useState(0);
  const [brandsPage, setBrandsPage] = useState(1);
  const [brandsLoading, setBrandsLoading] = useState(false);
  const [country, setCountry] = useState("US");
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState("name");

  // Default both date-filtered tabs to the current month on first load.
  const initialRange = useMemo(() => presetRange("last_7_days"), []);

  // Clicks state
  const [clicks, setClicks] = useState([]);
  const [clicksTotal, setClicksTotal] = useState(0);
  const [clicksTotals, setClicksTotals] = useState({ total: 0, converted: 0 });
  const [clicksPage, setClicksPage] = useState(1);
  const [clicksLoading, setClicksLoading] = useState(false);
  const [clicksPreset, setClicksPreset] = useState("last_7_days");
  const [clicksStart, setClicksStart] = useState(initialRange.start);
  const [clicksEnd, setClicksEnd] = useState(initialRange.end);
  const [clicksAdvertiser, setClicksAdvertiser] = useState("");
  const [clicksSearch, setClicksSearch] = useState("");

  // Commissions state
  const [comms, setComms] = useState([]);
  const [commsTotal, setCommsTotal] = useState(0);
  const [commsTotals, setCommsTotals] = useState({ count: 0, commission: 0, sales_amount: 0, by_currency: [] });
  const [commsPage, setCommsPage] = useState(1);
  const [commsLoading, setCommsLoading] = useState(false);
  const [commsPreset, setCommsPreset] = useState("last_7_days");
  const [commsStart, setCommsStart] = useState(initialRange.start);
  const [commsEnd, setCommsEnd] = useState(initialRange.end);
  const [commsAdvertiser, setCommsAdvertiser] = useState("");
  const [commsSearch, setCommsSearch] = useState("");
  const [commsState, setCommsState] = useState("");

  const pageSize = 50;

  // Apply a preset to a tab: resolve to a concrete YYYY-MM-DD range and
  // reset to page 1. "custom" leaves the current range editable as-is.
  const applyClicksPreset = useCallback((p) => {
    setClicksPreset(p);
    setClicksPage(1);
    if (p !== "custom") {
      const r = presetRange(p);
      setClicksStart(r.start);
      setClicksEnd(r.end);
    }
  }, []);
  const applyCommsPreset = useCallback((p) => {
    setCommsPreset(p);
    setCommsPage(1);
    if (p !== "custom") {
      const r = presetRange(p);
      setCommsStart(r.start);
      setCommsEnd(r.end);
    }
  }, []);

  const fetchBrands = useCallback(async () => {
    setBrandsLoading(true);
    try {
      // `country === "ALL"` is our sentinel for "no country filter" — drop
      // it from the params so the API returns the entire catalog. Same
      // semantics admin gets when picking "All countries".
      const params = { page: brandsPage, page_size: pageSize, sort };
      if (country && country !== "ALL") params.country = country;
      if (search.trim()) params.search = search.trim();
      const { data } = await API.get("/v1/cpc/brands", { params });
      setBrands(data.data || []);
      setBrandsTotal(data.total || 0);
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
      else toast.error(err.response?.data?.detail || "Failed to load Yieldkit brands");
    } finally {
      setBrandsLoading(false);
    }
  }, [brandsPage, country, sort, search]);

  const fetchClicks = useCallback(async () => {
    setClicksLoading(true);
    try {
      const params = { page: clicksPage, page_size: pageSize, network: "yieldkit" };
      if (clicksStart) params.start_date = clicksStart;
      if (clicksEnd) params.end_date = clicksEnd;
      if (clicksAdvertiser) params.advertiser_id = clicksAdvertiser;
      if (clicksSearch.trim()) params.search = clicksSearch.trim();
      const { data } = await API.get("/v1/cpc/clicks", { params });
      setClicks(data.data || []);
      setClicksTotal(data.total || 0);
      setClicksTotals(data.totals || { total: data.total || 0, converted: 0 });
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
      else toast.error(err.response?.data?.detail || "Failed to load clicks");
    } finally {
      setClicksLoading(false);
    }
  }, [clicksPage, clicksStart, clicksEnd, clicksAdvertiser, clicksSearch]);

  const fetchCommissions = useCallback(async () => {
    setCommsLoading(true);
    try {
      const params = { page: commsPage, page_size: pageSize, network: "yieldkit" };
      if (commsStart) params.start_date = commsStart;
      if (commsEnd) params.end_date = commsEnd;
      if (commsAdvertiser) params.advertiser_id = commsAdvertiser;
      if (commsState) params.state = commsState;
      if (commsSearch.trim()) params.search = commsSearch.trim();
      const { data } = await API.get("/v1/cpc/commissions", { params });
      setComms(data.data || []);
      setCommsTotal(data.total || 0);
      setCommsTotals(data.totals || { count: 0, commission: 0, sales_amount: 0, by_currency: [] });
    } catch (err) {
      if (err.response?.status === 403) setAccessDenied(true);
      else toast.error(err.response?.data?.detail || "Failed to load commissions");
    } finally {
      setCommsLoading(false);
    }
  }, [commsPage, commsStart, commsEnd, commsAdvertiser, commsState, commsSearch]);

  useEffect(() => {
    API.get("/v1/cpc/filters")
      .then((r) => setFilterOpts(r?.data || { advertisers: [], states: [] }))
      .catch(() => { /* degrade gracefully */ });
  }, []);

  useEffect(() => { if (tab === "brands") fetchBrands(); }, [tab, fetchBrands]);
  useEffect(() => {
    if (tab !== "clicks") return;
    const t = setTimeout(fetchClicks, 250);
    return () => clearTimeout(t);
  }, [tab, fetchClicks]);
  useEffect(() => {
    if (tab !== "commissions") return;
    const t = setTimeout(fetchCommissions, 250);
    return () => clearTimeout(t);
  }, [tab, fetchCommissions]);

  // Prime the Clicks + Commissions counts on first load so the tab badges
  // show real numbers immediately instead of "(0)" until each tab is opened.
  // (Brands loads via the active-tab effect above since it's the default.)
  useEffect(() => {
    fetchClicks();
    fetchCommissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totalForTab = tab === "brands" ? brandsTotal : tab === "clicks" ? clicksTotal : commsTotal;
  const totalPages = useMemo(
    () => Math.max(1, Math.ceil(totalForTab / pageSize)),
    [totalForTab]
  );
  const page = tab === "brands" ? brandsPage : tab === "clicks" ? clicksPage : commsPage;
  const setPage = tab === "brands" ? setBrandsPage : tab === "clicks" ? setClicksPage : setCommsPage;

  const copyLink = (link) => {
    try {
      navigator.clipboard.writeText(link);
      toast.success("Tracking link copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  const downloadCsv = async (kind) => {
    const isClicks = kind === "clicks";
    if (isClicks ? exportingClicks : exportingComms) return;
    if (isClicks) setExportingClicks(true);
    else setExportingComms(true);
    try {
      const params = new URLSearchParams();
      const start = isClicks ? clicksStart : commsStart;
      const end = isClicks ? clicksEnd : commsEnd;
      const advertiser = isClicks ? clicksAdvertiser : commsAdvertiser;
      const search = isClicks ? clicksSearch : commsSearch;
      if (start) params.set("start_date", start);
      if (end) params.set("end_date", end);
      if (advertiser) params.set("advertiser_id", advertiser);
      if (search.trim()) params.set("search", search.trim());
      if (!isClicks && commsState) params.set("state", commsState);
      const baseURL = API.defaults?.baseURL || "";
      const token = localStorage.getItem("token") || "";
      const path = isClicks ? "/v1/cpc/clicks/export.csv" : "/v1/cpc/commissions/export.csv";
      const resp = await fetch(`${baseURL}${path}?${params.toString()}`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `yieldkit-${kind}-${start}-to-${end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.message || "Export failed");
    } finally {
      if (isClicks) setExportingClicks(false);
      else setExportingComms(false);
    }
  };

  const commsPrimary = commsTotals.by_currency?.[0];
  const commsSym = ({ USD: "$", GBP: "£", EUR: "€" }[commsPrimary?.currency || "EUR"]) || "€";

  if (accessDenied) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <div className="max-w-md text-center space-y-3 px-4">
          <Lightning size={42} className="mx-auto text-[#002FA7]" weight="fill" />
          <h1 className="font-heading text-2xl font-bold">Yieldkit not enabled</h1>
          <p className="text-sm text-slate-600">
            Your account doesn't currently have a Yieldkit lease bound. Contact admin to enable Yieldkit.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6" data-testid="pub-cpc-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <p className="text-xs text-slate-500 font-bold tracking-[0.2em] uppercase">NETWORK</p>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold tracking-wider text-amber-900 bg-amber-100 border border-amber-200" title="All earnings are reported in Euros">
              {CURRENCY_CHIP}
            </span>
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-slate-900">
            <Lightning size={28} className="inline mr-2 -mt-1 text-[#002FA7]" weight="fill" />
            Yieldkit
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Affiliate programs from Yieldkit. Traffic is redirected via <code className="bg-slate-100 px-1 rounded font-mono text-[11px]">offers.goshoptoday.com</code>, clicks land instantly, commissions settle in EUR.
          </p>
        </div>
        {(tab === "clicks" || tab === "commissions") && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadCsv(tab)}
            disabled={(tab === "clicks" ? exportingClicks : exportingComms) || (tab === "clicks" ? clicksTotal : commsTotal) === 0}
            data-testid={`pub-cpc-export-${tab}`}
          >
            <DownloadSimple size={14} className="mr-1.5" />
            {(tab === "clicks" ? exportingClicks : exportingComms) ? "Exporting…" : "Export CSV"}
          </Button>
        )}
      </div>

      <Tabs value={tab} onValueChange={setTab} className="space-y-4">
        <TabsList>
          <TabsTrigger value="brands" data-testid="pub-cpc-tab-brands">Brands ({brandsTotal})</TabsTrigger>
          <TabsTrigger value="clicks" data-testid="pub-cpc-tab-clicks">Clicks ({clicksTotal})</TabsTrigger>
          <TabsTrigger value="commissions" data-testid="pub-cpc-tab-commissions">Commissions ({commsTotal})</TabsTrigger>
        </TabsList>

        {/* BRANDS */}
        <TabsContent value="brands" className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-slate-100">
            <div className="relative w-64">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search advertisers…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") { setBrandsPage(1); fetchBrands(); }
                }}
                className="pl-8 h-9 text-sm"
                data-testid="cpc-search-input"
              />
            </div>
            <Select value={country} onValueChange={(v) => { setCountry(v); setBrandsPage(1); }}>
              <SelectTrigger className="w-36 h-9" data-testid="cpc-country-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {COUNTRIES.map((c) => (
                  <SelectItem key={c} value={c}>
                    {c === "ALL" ? "All countries" : c}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sort} onValueChange={(v) => { setSort(v); setBrandsPage(1); }}>
              <SelectTrigger className="w-36 h-9" data-testid="cpc-sort-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="name">Name (A-Z)</SelectItem>
                <SelectItem value="-name">Name (Z-A)</SelectItem>
                <SelectItem value="-ecpc">eCPC (high → low)</SelectItem>
                <SelectItem value="ecpc">eCPC (low → high)</SelectItem>
              </SelectContent>
            </Select>
            <div className="flex-1" />
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="cpc-prev-btn"><CaretLeft size={12} /></Button>
              <span className="text-xs text-slate-500 font-mono px-2">Page {page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="cpc-next-btn"><CaretRight size={12} /></Button>
            </div>
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Advertiser</th>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Countries</th>
                  <th className="text-left px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Category</th>
                  <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                    {country && country !== "ALL" ? `Commission (${country})` : "Best Commission"}
                  </th>
                  <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">
                    {country && country !== "ALL" ? `eCPC (${country})` : "Best eCPC"}
                  </th>
                  <th className="text-right px-4 py-2.5 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Tracking Link</th>
                </tr>
              </thead>
              <tbody>
                {brandsLoading && <SkeletonRows rows={8} widths={["w-40", "w-24", "w-20", "w-16", "w-16", "w-24"]} aligns={["left", "left", "left", "right", "right", "right"]} />}
                {!brandsLoading && brands.length === 0 && (
                  <tr><td colSpan={6} className="px-4 py-12 text-center text-slate-500">
                    No brands for <strong>{country === "ALL" ? "any country" : country}</strong> yet. Admin may still be syncing the catalog — check back shortly.
                  </td></tr>
                )}
                {brands.map((b) => {
                  // Per-country commission lookup. When the filter is a
                  // single country, we display that country's commission
                  // and CPC straight from country_metrics. When "ALL" is
                  // selected, pick the country with the highest commission
                  // as the headline ("best market") so pubs see the
                  // strongest revenue signal at a glance.
                  const cm = b.country_metrics || {};
                  const selected = (country && country !== "ALL") ? country : null;
                  let displayCountry = selected;
                  let metric = selected ? cm[selected] : null;
                  if (!metric) {
                    // Find best market = highest commission across all countries
                    let best = null, bestKey = null;
                    for (const [k, v] of Object.entries(cm)) {
                      const c = Number(v?.commission || 0);
                      if (best === null || c > Number(best.commission || 0)) {
                        best = v; bestKey = k;
                      }
                    }
                    metric = best;
                    displayCountry = bestKey;
                  }
                  const commission = Number(metric?.commission || 0);
                  const cpc = Number(metric?.cpc || 0);
                  // Per YK spec: country_metrics values are ALWAYS in EUR,
                  // even when the advertiser default (b.currency) is USD.
                  // Default to "EUR" so the badge stays correct if the field
                  // is missing on legacy rows.
                  const metricCurrency = metric?.currency || "EUR";
                  return (
                    <tr key={b.advertiser_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`cpc-brand-${b.advertiser_id}`}>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-2.5">
                          {b.image && <img src={b.image} alt={b.name} className="w-7 h-7 object-contain rounded bg-white border border-slate-100" />}
                          <div>
                            <div className="font-semibold text-slate-900">{b.name}</div>
                            <div className="text-[11px] text-slate-500">{b.domain}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-2.5">
                        {/* Full country list as chips. When a specific country is filtered, that chip is highlighted. */}
                        <div className="flex flex-wrap gap-1 max-w-[180px]">
                          {(b.countries || []).slice(0, 6).map((c) => (
                            <span
                              key={c}
                              className={`text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded font-mono ${
                                selected === c
                                  ? "bg-[#002FA7] text-white"
                                  : displayCountry === c
                                  ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                  : "bg-slate-100 text-slate-600"
                              }`}
                              data-testid={`cpc-brand-country-${b.advertiser_id}-${c}`}
                            >
                              {c}
                            </span>
                          ))}
                          {(b.countries || []).length > 6 && (
                            <span className="text-[9px] text-slate-400 font-bold uppercase tracking-wider px-1">
                              +{b.countries.length - 6}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-[11px] text-slate-600">{b.category || "-"}</td>
                      <td className="px-4 py-2.5 text-right font-mono text-sm" data-testid={`cpc-brand-commission-${b.advertiser_id}`}>
                        {commission > 0 ? (
                          <div className="flex items-center justify-end gap-1">
                            <span className="text-[#002FA7] font-bold">
                              {metricCurrency === "EUR" ? "€" : metricCurrency === "USD" ? "$" : ""}
                              {commission.toFixed(2)}
                            </span>
                            {displayCountry && !selected && (
                              <span className="text-[9px] uppercase tracking-wider text-emerald-700 font-bold">
                                {displayCountry}
                              </span>
                            )}
                          </div>
                        ) : "-"}
                      </td>
                      <td className="px-4 py-2.5 text-right font-mono text-sm text-slate-600">
                        {cpc > 0 ? `${metricCurrency === "EUR" ? "€" : metricCurrency === "USD" ? "$" : ""}${cpc.toFixed(3)}` : "-"}
                      </td>
                      <td className="px-4 py-2.5 text-right">
                        <div className="flex justify-end gap-1">
                          <Button variant="outline" size="sm" className="h-7 text-[11px] px-2" onClick={() => copyLink(b.tracking_link)} title={b.tracking_link} data-testid={`cpc-copy-${b.advertiser_id}`}>
                            <Copy size={12} className="mr-1" /> Copy
                          </Button>
                          <Button variant="outline" size="sm" className="h-7 px-2" onClick={() => window.open(b.tracking_link, "_blank", "noopener")}>
                            <LinkSimple size={12} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* CLICKS */}
        <TabsContent value="clicks" className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-slate-100">
            <DateRangeFilter
              preset={clicksPreset}
              onPreset={applyClicksPreset}
              start={clicksStart}
              end={clicksEnd}
              onStart={(v) => { setClicksPreset("custom"); setClicksStart(v); setClicksPage(1); }}
              onEnd={(v) => { setClicksPreset("custom"); setClicksEnd(v); setClicksPage(1); }}
              testidPrefix="cpc-clicks"
            />
            <Select
              value={clicksAdvertiser || ALL}
              onValueChange={(v) => { setClicksAdvertiser(v === ALL ? "" : v); setClicksPage(1); }}
            >
              <SelectTrigger className="w-44 h-9" data-testid="cpc-clicks-offer">
                <SelectValue placeholder="All offers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All offers</SelectItem>
                {filterOpts.advertisers.map((a) => (
                  <SelectItem key={a.advertiser_id} value={a.advertiser_id}>{a.name || a.advertiser_id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-48">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                value={clicksSearch}
                onChange={(e) => { setClicksSearch(e.target.value); setClicksPage(1); }}
                placeholder="RID / sub_id / offer"
                className="pl-8 h-9 text-sm"
                data-testid="cpc-clicks-search"
              />
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}><CaretLeft size={12} /></Button>
              <span className="text-xs text-slate-500 font-mono px-2">Page {page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><CaretRight size={12} /></Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm" data-testid="cpc-clicks-rollup">
            <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Clicks (period)</div>
              <div className="font-heading text-xl font-bold text-slate-900 mt-1">{(clicksTotals.total ?? clicksTotal).toLocaleString()}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{clicksStart} → {clicksEnd}</div>
            </div>
            <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Converted</div>
              <div className="font-heading text-xl font-bold text-emerald-700 mt-1">{(clicksTotals.converted ?? 0).toLocaleString()}</div>
            </div>
            {filterOpts.account_label ? (
              <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
                <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">YK Account</div>
                <div className="font-heading text-sm font-semibold text-slate-900 mt-1">{filterOpts.account_label}</div>
              </div>
            ) : null}
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Date</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Offer</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Click ID (RID)</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sub IDs</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Geo / IP</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">User Agent</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {clicksLoading && <SkeletonRows rows={8} widths={["w-28", "w-32", "w-20", "w-24", "w-20", "w-32", "w-16"]} />}
                {!clicksLoading && clicks.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No clicks for this range. Click any Yieldkit brand link to see data here within seconds.</td></tr>
                )}
                {clicks.map((r) => (
                  <tr key={r.rid} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 text-[11px] text-slate-900 whitespace-nowrap">{r.click_date ? new Date(r.click_date).toLocaleString() : "-"}</td>
                    <td className="px-3 py-2 text-[11px] font-semibold text-slate-900">{r.advertiser_name || r.advertiser_id || "—"}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-[#002FA7] font-bold">{r.rid}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-600">
                      {[r.sub_id1, r.sub_id2, r.sub_id3].filter(Boolean).join(" · ") || "—"}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-slate-600">
                      <div>{r.country || "—"}</div>
                      <div className="font-mono text-[10px] text-slate-400">{r.ip || ""}</div>
                    </td>
                    <td className="px-3 py-2 text-[10px] text-slate-500 max-w-[200px] truncate" title={r.ua}>{r.ua || "—"}</td>
                    <td className="px-3 py-2 text-[11px]">
                      {r.converted ? (
                        <span className="inline-flex items-center gap-1 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">
                          <CheckCircle size={12} weight="fill" /> Converted
                        </span>
                      ) : r.redirect_ok !== false ? (
                        <span className="text-slate-500 text-[10px]">{r.source === "yk_api" ? "From YK API" : "Redirected"}</span>
                      ) : (
                        <span className="text-rose-700 text-[10px] font-semibold">Failed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        {/* COMMISSIONS */}
        <TabsContent value="commissions" className="space-y-3">
          <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-slate-100">
            <DateRangeFilter
              preset={commsPreset}
              onPreset={applyCommsPreset}
              start={commsStart}
              end={commsEnd}
              onStart={(v) => { setCommsPreset("custom"); setCommsStart(v); setCommsPage(1); }}
              onEnd={(v) => { setCommsPreset("custom"); setCommsEnd(v); setCommsPage(1); }}
              testidPrefix="cpc-commissions"
            />
            <Select
              value={commsAdvertiser || ALL}
              onValueChange={(v) => { setCommsAdvertiser(v === ALL ? "" : v); setCommsPage(1); }}
            >
              <SelectTrigger className="w-44 h-9" data-testid="cpc-comms-offer">
                <SelectValue placeholder="All offers" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All offers</SelectItem>
                {filterOpts.advertisers.map((a) => (
                  <SelectItem key={a.advertiser_id} value={a.advertiser_id}>{a.name || a.advertiser_id}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={commsState || ALL}
              onValueChange={(v) => { setCommsState(v === ALL ? "" : v); setCommsPage(1); }}
            >
              <SelectTrigger className="w-36 h-9" data-testid="cpc-comms-state">
                <SelectValue placeholder="All statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>All statuses</SelectItem>
                {(filterOpts.states || []).map((s) => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="relative w-48">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-2.5 text-slate-400" />
              <Input
                value={commsSearch}
                onChange={(e) => { setCommsSearch(e.target.value); setCommsPage(1); }}
                placeholder="Event / commission ID"
                className="pl-8 h-9 text-sm"
                data-testid="cpc-comms-search"
              />
            </div>
            <div className="flex-1" />
            <div className="flex items-center gap-1">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}><CaretLeft size={12} /></Button>
              <span className="text-xs text-slate-500 font-mono px-2">Page {page} / {totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}><CaretRight size={12} /></Button>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-sm" data-testid="cpc-comms-rollup">
            <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Conversions (period)</div>
              <div className="font-heading text-xl font-bold text-slate-900 mt-1">{(commsTotals.count ?? commsTotal).toLocaleString()}</div>
              <div className="text-[10px] text-slate-400 mt-0.5">{commsStart} → {commsEnd}</div>
            </div>
            <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Commission</div>
              <div className="font-heading text-xl font-bold text-[#002FA7] mt-1">
                {commsSym}{(commsPrimary?.commission ?? commsTotals.commission ?? 0).toFixed(2)}
              </div>
            </div>
            <div className="rounded-md border border-slate-200 bg-white px-4 py-3">
              <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sales amount</div>
              <div className="font-heading text-xl font-bold text-slate-900 mt-1">
                {commsSym}{(commsPrimary?.sales_amount ?? commsTotals.sales_amount ?? 0).toFixed(2)}
              </div>
            </div>
          </div>

          <div className="border border-slate-200 rounded-md overflow-hidden bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sale Date</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Advertiser</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">RID</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">SubId2</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Sale</th>
                  <th className="text-right px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Commission</th>
                  <th className="text-left px-3 py-2 text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Status</th>
                </tr>
              </thead>
              <tbody>
                {commsLoading && <SkeletonRows rows={8} widths={["w-28", "w-32", "w-20", "w-16", "w-14", "w-14", "w-16"]} aligns={["left", "left", "left", "left", "right", "right", "left"]} />}
                {!commsLoading && comms.length === 0 && (
                  <tr><td colSpan={7} className="px-4 py-12 text-center text-slate-500">No commissions for this range yet. Commissions settle after conversions are reconciled with Yieldkit.</td></tr>
                )}
                {comms.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="px-3 py-2 text-[11px] text-slate-900">{r.sales_date ? new Date(r.sales_date).toLocaleString() : "-"}</td>
                    <td className="px-3 py-2 font-semibold text-slate-900 text-xs">{r.advertiser_name || r.advertiser_id}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-[#002FA7] font-bold">{r.rid || r.sub_id || "-"}</td>
                    <td className="px-3 py-2 font-mono text-[10px] text-slate-600">{r.sub_id2 || "-"}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs">{r.sales_amount_eur > 0 ? `€${Number(r.sales_amount_eur).toFixed(2)}` : "-"}</td>
                    <td className="px-3 py-2 text-right font-mono text-xs font-semibold text-[#002FA7]">{r.commission_eur > 0 ? `€${Number(r.commission_eur).toFixed(2)}` : "-"}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold tracking-wider uppercase ${
                        r.state === "CONFIRMED" ? "bg-emerald-100 text-emerald-700"
                        : r.state === "REJECTED" ? "bg-rose-100 text-rose-700"
                        : r.state === "OPEN" || r.state === "DELAYED" ? "bg-amber-100 text-amber-700"
                        : "bg-slate-100 text-slate-500"
                      }`}>{r.state || "-"}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Table skeleton placeholder shown while a tab's data is loading. Renders
// `rows` shimmer rows, one Skeleton bar per column sized by `widths` and
// aligned per `aligns` ("left" default | "right") so the placeholder tracks
// the real table layout instead of a single centered "Loading…" line.
function SkeletonRows({ rows = 6, widths = [], aligns = [] }) {
  const cols = widths.length;
  return (
    <>
      {Array.from({ length: rows }).map((_, r) => (
        <tr key={r} className="border-b border-slate-100">
          {widths.map((w, c) => (
            <td key={c} className="px-3 py-2.5">
              <Skeleton className={`h-4 ${w} ${aligns[c] === "right" ? "ml-auto" : ""}`} />
            </td>
          ))}
        </tr>
      ))}
      {/* Keep colSpan cell for browsers that need at least one full-width row */}
      {cols === 0 && <tr><td className="px-4 py-12" /></tr>}
    </>
  );
}

// Date-range presets offered in the Clicks / Commissions filter dropdown.
// "custom" reveals the raw date inputs; every other value resolves to a
// concrete range via presetRange().
const DATE_PRESETS = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "last_7_days", label: "Last 7 days" },
  { value: "last_30_days", label: "Last 30 days" },
  { value: "custom", label: "Custom" },
];

// Format a Date to `YYYY-MM-DD` using LOCAL date parts (not UTC) so the
// range matches what the user sees on their calendar.
function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

// Resolve a preset key to a concrete { start, end } range (inclusive).
// Returns null for "custom" (caller keeps the existing range).
function presetRange(preset) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  switch (preset) {
    case "today":
      return { start: fmtDate(today), end: fmtDate(today) };
    case "yesterday": {
      const y = new Date(today);
      y.setDate(y.getDate() - 1);
      return { start: fmtDate(y), end: fmtDate(y) };
    }
    case "this_month": {
      const s = new Date(today.getFullYear(), today.getMonth(), 1);
      return { start: fmtDate(s), end: fmtDate(today) };
    }
    case "last_month": {
      const s = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      const e = new Date(today.getFullYear(), today.getMonth(), 0); // last day of prev month
      return { start: fmtDate(s), end: fmtDate(e) };
    }
    case "last_7_days": {
      const s = new Date(today);
      s.setDate(s.getDate() - 6); // 7 days inclusive of today
      return { start: fmtDate(s), end: fmtDate(today) };
    }
    case "last_30_days": {
      const s = new Date(today);
      s.setDate(s.getDate() - 29); // 30 days inclusive of today
      return { start: fmtDate(s), end: fmtDate(today) };
    }
    default:
      return { start: "", end: "" };
  }
}

// Inline date-range control shared by the Clicks and Commissions tabs.
// A preset dropdown drives the common ranges; picking "Custom" (or editing
// a date directly) exposes native `YYYY-MM-DD` inputs that map straight to
// the API's start_date / end_date params (end_date is inclusive-of-day
// server-side).
function DateRangeFilter({ preset, onPreset, start, end, onStart, onEnd, testidPrefix }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-bold">Period</span>
      <Select value={preset} onValueChange={onPreset}>
        <SelectTrigger className="w-36 h-9" data-testid={`${testidPrefix}-preset`}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DATE_PRESETS.map((p) => (
            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      {preset === "custom" ? (
        <>
          <Input
            type="date"
            value={start}
            max={end || undefined}
            onChange={(e) => onStart(e.target.value)}
            className="h-9 w-40 text-sm"
            data-testid={`${testidPrefix}-start`}
          />
          <span className="text-slate-400 text-xs">→</span>
          <Input
            type="date"
            value={end}
            min={start || undefined}
            onChange={(e) => onEnd(e.target.value)}
            className="h-9 w-40 text-sm"
            data-testid={`${testidPrefix}-end`}
          />
        </>
      ) : (
        (start || end) && (
          <span className="text-xs text-slate-500 font-mono" data-testid={`${testidPrefix}-range`}>
            {start || "…"} → {end || "…"}
          </span>
        )
      )}
    </div>
  );
}
